import { useEffect } from 'react';
import { useCoursesUrlFilters, useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import { LinkButton, LoadingIndicator, Pagination } from 'components/common';
import {
  PageEmptyIndicator,
  PageSectionHeader,
  PageShowAllLink
} from 'components/page';
import { CoursesAdminTile, CoursesSortBy, CoursesWrapper } from '.';

import type { ViewProps } from 'types';
import type { GetCoursesRes } from 'shared-types/responses';

type Props = ViewProps & {
  onFilterDataReceived?: (data: any) => void;
};

const CoursesViewAdministered = ({
  defaultSort = 'updated',
  description,
  hideIfNoData = false,
  isHomeView = false,
  limitPerPage = 12,
  onFilterDataReceived,
  onNoData = null,
  queryParams = '',
  showAllLinkHref = null,
  title,
  titleShowCount = true
}: Props) => {
  const { messages } = useReduxState();

  const { currentPage, getUrl, onPageChange, ready } = useCoursesUrlFilters({
    defaultSort,
    queryParams
  });

  const { data, loading, updateData } = useApiData<GetCoursesRes>({
    onDataUpdated: (data) => {
      // filter data is sent to parent
      if (onFilterDataReceived) {
        if (data?.coursesFilterOptions) {
          onFilterDataReceived(data?.coursesFilterOptions);
        } else {
          onFilterDataReceived(null);
        }
      }
    },
    onlyIf: ready,
    url: getUrl('/admin/courses?courseType=administered', limitPerPage)
  });

  useEffect(() => {
    const hasData = !loading && Boolean(data?.courses?.length);
    if (!loading && onNoData) onNoData(hasData);
  }, [data, loading]);

  return loading || !ready ? (
    <LoadingIndicator />
  ) : data?.numberOfCourses > 0 ? (
    <>
      <PageSectionHeader
        description={description}
        title={title || messages.coursesAdministered}
        titleCount={titleShowCount ? data?.numberOfCourses : undefined}
      >
        {isHomeView ? null : (
          <CoursesSortBy
            options={[
              { text: messages.commonAlphabetic, value: 'alphabetic' },
              { text: messages.courseLastUpdated, value: 'updated' }
            ]}
            defaultSort={defaultSort}
          />
        )}
      </PageSectionHeader>

      <CoursesWrapper isHomeView={isHomeView}>
        {data?.courses.map((course) => (
          <div className="course" key={course.id}>
            <CoursesAdminTile course={course} updateCourses={updateData} />
          </div>
        ))}
      </CoursesWrapper>

      {data?.pages > 1 && !isHomeView ? (
        <Pagination
          currentPage={currentPage}
          numberOfPages={data?.pages || 1}
          onPageChange={onPageChange}
        />
      ) : showAllLinkHref && data?.numberOfCourses > data?.courses?.length ? (
        <PageShowAllLink
          href={showAllLinkHref}
          numberOfItems={data?.numberOfCourses}
        />
      ) : null}
    </>
  ) : hideIfNoData ? null : (
    <>
      <PageSectionHeader
        description={description}
        title={title || messages.coursesAdministered}
        titleCount={titleShowCount ? 0 : undefined}
      />

      <PageEmptyIndicator
        icon="book"
        title={messages.coursesNoCoursesFound}
        description={messages.coursesNoCoursesAdministered}
      >
        <LinkButton
          category="light"
          href="/courses?type=all"
          size="small"
          text={messages.coursesSeeAllCourses}
        />
      </PageEmptyIndicator>
    </>
  );
};

export default CoursesViewAdministered;
